import { PspRules } from "@CommonTypes/backend/PspRules";
import { RulesSection } from "./components/RulesSection";
import { Grid, HorizontalDivider } from "src/elements";
import { RuleToggle } from "./components/RuleToggle";
import { RuleRange } from "./components/RuleRange";
import { RuleRadios } from "./components/RuleRadios";

export interface Props {
  isDisabled?: boolean;

  // Fixture
  canAdjustShelfNotches: PspRules["fixture"]["can_adjust_shelf_notches"];
  setCanAdjustShelfNotches: (value: PspRules["fixture"]["can_adjust_shelf_notches"]) => void;

  canAddOrDeleteShelves: PspRules["fixture"]["can_add_or_delete_shelves"];
  setCanAddOrDeleteShelves: (value: PspRules["fixture"]["can_add_or_delete_shelves"]) => void;

  shelvesSameHeightAsBasePog: PspRules["fixture"]["shelves_same_height_as_base_pog"];
  setShelvesSameHeightAsBasePog: (
    value: PspRules["fixture"]["shelves_same_height_as_base_pog"],
  ) => void;

  verticalOverlap: PspRules["fixture"]["vertical_overlap"];
  setVerticalOverlap: (value: PspRules["fixture"]["vertical_overlap"]) => void;

  mergedShelfWidthTolerancePct: PspRules["fixture"]["merged_shelf_width_tolerance_pct"];
  setMergedShelfWidthTolerancePct: (
    value: PspRules["fixture"]["merged_shelf_width_tolerance_pct"],
  ) => void;

  // Range
  canAddNonCoreRangeProducts: PspRules["range"]["can_add_non_core_range_products"];
  setCanAddNonCoreRangeProducts: (
    value: PspRules["range"]["can_add_non_core_range_products"],
  ) => void;

  canApplySqueezeToPlacedProducts: PspRules["range"]["can_apply_squeeze_to_placed_products"];
  setCanApplySqueezeToPlacedProducts: (
    value: PspRules["range"]["can_apply_squeeze_to_placed_products"],
  ) => void;

  // Merchandising
  verticalBlockingMode: PspRules["merchandising"]["vertical_blocking_mode"];
  setVerticalBlockingMode: (value: PspRules["merchandising"]["vertical_blocking_mode"]) => void;

  canBreakCasesTraysIfCoreRangeNotFit: PspRules["merchandising"]["can_break_cases_trays_if_core_range_not_fit"];
  setCanBreakCasesTraysIfCoreRangeNotFit: (
    value: PspRules["merchandising"]["can_break_cases_trays_if_core_range_not_fit"],
  ) => void;

  canFillExtraSpaceNextToCasesTraysWithLooseProducts: PspRules["merchandising"]["can_fill_extra_space_next_to_cases_trays_with_loose_products"];
  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts: (
    value: PspRules["merchandising"]["can_fill_extra_space_next_to_cases_trays_with_loose_products"],
  ) => void;

  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays: PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"];
  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays: (
    value: PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"],
  ) => void;

  leverageAlternativeOrientations: PspRules["merchandising"]["leverage_alternative_orientations"];
  setLeverageAlternativeOrientations: (
    value: PspRules["merchandising"]["leverage_alternative_orientations"],
  ) => void;

  extraFacingMarginalValue: number;
  setExtraFacingMarginalValue: (value: number) => void;

  snaking: PspRules["merchandising"]["snaking"];
  setSnaking: (value: PspRules["merchandising"]["snaking"]) => void;

  cdtStacking: PspRules["merchandising"]["cdt_stacking"];
  setCdtStacking: (value: PspRules["merchandising"]["cdt_stacking"]) => void;

  itemsSqueezeOption: PspRules["merchandising"]["items_squeeze_option"];
  setItemsSqueezeOption: (value: PspRules["merchandising"]["items_squeeze_option"]) => void;
}

export const GeneralPlanogramRules = ({
  isDisabled,

  // Fixture
  canAdjustShelfNotches,
  setCanAdjustShelfNotches,
  canAddOrDeleteShelves,
  setCanAddOrDeleteShelves,
  shelvesSameHeightAsBasePog,
  setShelvesSameHeightAsBasePog,
  verticalOverlap,
  setVerticalOverlap,
  mergedShelfWidthTolerancePct,
  setMergedShelfWidthTolerancePct,

  // Range
  canAddNonCoreRangeProducts,
  setCanAddNonCoreRangeProducts,
  canApplySqueezeToPlacedProducts,
  setCanApplySqueezeToPlacedProducts,

  // Merchandising
  verticalBlockingMode,
  setVerticalBlockingMode,
  canBreakCasesTraysIfCoreRangeNotFit,
  setCanBreakCasesTraysIfCoreRangeNotFit,
  canFillExtraSpaceNextToCasesTraysWithLooseProducts,
  setCanFillExtraSpaceNextToCasesTraysWithLooseProducts,
  howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  leverageAlternativeOrientations,
  setLeverageAlternativeOrientations,
  extraFacingMarginalValue,
  setExtraFacingMarginalValue,
  snaking,
  setSnaking,
  cdtStacking,
  setCdtStacking,
  itemsSqueezeOption,
  setItemsSqueezeOption,
}: Props) => {
  const normalizedExtraFacingMarginalValue: number = extraFacingMarginalValue ?? 0;

  return (
    <Grid
      width="100%"
      gap="20px"
      areas={`
        "fixtures merchandising"
        "range    merchandising"
      `}
    >
      {/* Fixture Section */}
      <RulesSection title="Fixtures" area="fixtures" isDisabled={isDisabled}>
        <RuleToggle
          title="Can shelf notches be adjusted in stores for this review?"
          value={canAdjustShelfNotches}
          setValue={setCanAdjustShelfNotches}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we add or delete shelves to make products fit?"
          value={canAddOrDeleteShelves}
          setValue={setCanAddOrDeleteShelves}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we attempt to make shelves the same height as base pog?"
          value={shelvesSameHeightAsBasePog}
          setValue={setShelvesSameHeightAsBasePog}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can products overlap with shelves vertically?"
          value={verticalOverlap}
          setValue={setVerticalOverlap}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRange
          title="What is the merged shelf width tolerance percentage?"
          min={0}
          max={30}
          step={0.01}
          value={mergedShelfWidthTolerancePct}
          setValue={setMergedShelfWidthTolerancePct}
          leftLabel="0%"
          rightLabel="30%"
          isDisabled={isDisabled}
        />
      </RulesSection>

      {/* Merchandising Section */}
      <RulesSection title="Merchandising" area="merchandising" isDisabled={isDisabled}>
        <RuleToggle
          title="Use vertical blocking mode?"
          value={verticalBlockingMode}
          setValue={setVerticalBlockingMode}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we break trays if a core range product cannot fit otherwise?"
          value={canBreakCasesTraysIfCoreRangeNotFit}
          setValue={setCanBreakCasesTraysIfCoreRangeNotFit}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we fill extra space next to cases / trays with loose products?"
          value={canFillExtraSpaceNextToCasesTraysWithLooseProducts}
          setValue={setCanFillExtraSpaceNextToCasesTraysWithLooseProducts}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRadios
          title="How should loose products be placed with respect to their cases / trays?"
          options={[
            { label: "Behind", value: "BEHIND" },
            { label: "Front", value: "FRONT" },
          ]}
          value={howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
          setValue={setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we leverage alternative orientations to fill empty space on a shelf?"
          value={leverageAlternativeOrientations}
          setValue={setLeverageAlternativeOrientations}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRange
          title="Extra Facing Marginal Value"
          min={0}
          max={1}
          step={0.01}
          value={normalizedExtraFacingMarginalValue}
          setValue={setExtraFacingMarginalValue}
          leftLabel="0"
          rightLabel="1"
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Allow snaking?"
          value={snaking}
          setValue={setSnaking}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Allow CDT stacking?"
          value={cdtStacking}
          setValue={setCdtStacking}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleRadios
          title="Should we squeeze product dimensions?"
          options={[
            { label: "No Squeeze", value: "none" },
            { label: "Squeeze All", value: "all" },
          ]}
          value={itemsSqueezeOption}
          setValue={setItemsSqueezeOption}
          isDisabled={isDisabled}
        />
      </RulesSection>

      {/* Range Section */}
      <RulesSection title="Range" area="range" isDisabled={isDisabled}>
        <RuleToggle
          title="Can we add non-core range products to fill underutilised shelf space?"
          value={canAddNonCoreRangeProducts}
          setValue={setCanAddNonCoreRangeProducts}
          isDisabled={isDisabled}
        />

        <HorizontalDivider />

        <RuleToggle
          title="Can we squeeze already placed products to make room for new products?"
          value={canApplySqueezeToPlacedProducts}
          setValue={setCanApplySqueezeToPlacedProducts}
          isDisabled={isDisabled}
        />
      </RulesSection>
    </Grid>
  );
};
